import type { UnitsMode } from '../types';

const UNITS_STORAGE_KEY = 'ontheway_units_mode_v1';
const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM']);

/** First-run units match iOS: imperial only for US, Liberia, Myanmar. */
export function resolveDefaultUnitSystem(locale = 'en-US'): UnitsMode {
  const region = locale.match(/[-_]([A-Za-z]{2}|\d{3})(?:$|[-_])/i)?.[1]?.toUpperCase();
  return region && IMPERIAL_REGIONS.has(region) ? 'imperial' : 'metric';
}

export function getDefaultUnits(): UnitsMode {
  try {
    const saved = localStorage.getItem(UNITS_STORAGE_KEY);
    if (saved === 'imperial' || saved === 'metric') return saved;
    return resolveDefaultUnitSystem(navigator.language || Intl.DateTimeFormat().resolvedOptions().locale || 'en-US');
  } catch {
    return resolveDefaultUnitSystem();
  }
}

export function saveUnitsPreference(mode: UnitsMode): void {
  try {
    localStorage.setItem(UNITS_STORAGE_KEY, mode);
  } catch {
    // ignore
  }
}

export function celsiusToFahrenheit(c: number): number {
  return c * 1.8 + 32;
}

export function formatTemp(c: number | null | undefined, mode: UnitsMode): string {
  if (c == null) return '—';
  const val = mode === 'imperial' ? celsiusToFahrenheit(c) : c;
  return `${Math.round(val)}°`;
}

export function formatTempFull(c: number | null | undefined, mode: UnitsMode): string {
  if (c == null) return '—';
  const val = mode === 'imperial' ? celsiusToFahrenheit(c) : c;
  return `${Math.round(val)}°${mode === 'imperial' ? 'F' : 'C'}`;
}

export function formatDistance(metres: number, mode: UnitsMode): { primary: string; secondary: string } {
  const mi = metres / 1609.344;
  const km = metres / 1000;

  if (mode === 'imperial') {
    const p = mi < 100 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
    const s = km < 100 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
    return { primary: p, secondary: s };
  } else {
    const p = km < 100 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
    const s = mi < 100 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
    return { primary: p, secondary: s };
  }
}

export function formatWind(kmh: number | null | undefined, mode: UnitsMode): string {
  if (kmh == null) return '—';
  if (mode === 'imperial') {
    const mph = Math.round(kmh * 0.621371);
    return `${mph} mph`;
  }
  return `${Math.round(kmh)} km/h`;
}

export function formatWindSpeedOnly(kmh: number | null | undefined, mode: UnitsMode): string {
  if (kmh == null) return '—';
  if (mode === 'imperial') {
    return `${Math.round(kmh * 0.621371)}`;
  }
  return `${Math.round(kmh)}`;
}

export function formatVisibility(metres: number | null | undefined, mode: UnitsMode): string {
  if (metres == null) return '—';
  if (mode === 'imperial') {
    const mi = metres / 1609.344;
    if (mi < 0.1) {
      const feet = Math.round(metres * 3.28084);
      return `${feet} ft`;
    }
    if (mi < 10) return `${mi.toFixed(1)} mi`;
    return `${Math.round(mi)} mi`;
  } else {
    if (metres < 1000) return `${Math.round(metres)} m`;
    const km = metres / 1000;
    if (km < 10) return `${km.toFixed(1)} km`;
    return `${Math.round(km)} km`;
  }
}

export function formatDuration(sec: number): string {
  const totalMin = Math.round(sec / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

export function formatClockTime(d: Date, withAmPm: boolean = true): string {
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).replace(withAmPm ? '' : /\s*(AM|PM)$/, '');
}
